'use client';
import Container from '@/components/layout/Container';
import Image from 'next/image';
import ExternalLink from '@/components/elements/ExternalLink';
import { useAppPathname } from '@/lib/hooks/use-app-pathname';
import { revealed } from '@/lib/animation/revealed';

export default function ColumnsOnly({
  block,
  position,
  backgroundColor,
  textColor,
  microsite,
  animationsEnabled
}) {
  const { title, description, columns, _metadata } = block.columns_only;
  const pathname = useAppPathname();
  const isMicrosite = microsite || pathname.startsWith('/feature/');

  return (
    <section
      id={_metadata.uid + '_' + position}
      data-name={'highlight-columns_only'}
      className={`overflow-hidden bg-[${backgroundColor}] text-[${textColor}] py-20 text-center ${
        isMicrosite ? `sm:py-30 lg:m-5 lg:py-40` : `sm:py-30`
      }`}
    >
      <Container fullPage={false}>
        <div className="mx-auto sm:max-w-165 lg:max-w-full">
          <div className={`${isMicrosite ? 'md:px-5' : 'px-5'} px-5 sm:px-10`}>
            <revealed.div
              className={`${isMicrosite ? 'mb-10' : 'mb-15'}`}
              animate={animationsEnabled}
              fade={true}
            >
              <h2
                className={`${isMicrosite ? 'h3 md:h2 lg:h1 mb-4' : 'h3 lg:h2 mb-4'}`}
              >
                {title}
              </h2>
              <div className="body-r lg:sh8">{description}</div>
            </revealed.div>
            <revealed.div
              className={`grid grid-cols-1 gap-x-20 gap-y-10 ${isMicrosite ? 'md:grid-cols-3' : 'lg:grid-cols-3'}`}
              animate={animationsEnabled}
              delayMs={500}
              fade={true}
            >
              {columns.map((column, index) => {
                return (
                  <div key={index}>
                    <Image
                      src={column.icon.url}
                      width="64"
                      height="64"
                      sizes="64px"
                      alt={column.icon.title.replace(/\.[^/.]+$/, '')}
                      className="mx-auto lg:mb-4"
                    />
                    <h3
                      className={`${isMicrosite ? 'h5 mb-2 lg:mb-4' : 'sh10 lg:sh9'} mb-2 lg:mb-4`}
                    >
                      {column.title}
                    </h3>
                    <div className="body-s lg:body-r">{column.description}</div>
                    {(column?.cta?.external_link ||
                      column?.cta?.internal_link) && (
                      <ExternalLink
                        externalLink={column?.cta?.external_link}
                        linkType={column?.cta?.link_type}
                        linkStyling={`btn btn-tertiary-dark inline-block mt-4`}
                        internalLink={column?.cta?.internal_link[0]}
                        linkText={column?.cta?.button_copy}
                      />
                    )}
                  </div>
                );
              })}
            </revealed.div>
          </div>
        </div>
      </Container>
    </section>
  );
}
